import { useState, useRef, useEffect, useTransition } from 'react';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import Image from 'next/image';

import { NotificationsList } from './notification-list.ui';

import { useUnreadCount } from '@/slw/entities/notification/notification.query';
import { useMarkAllAsRead } from '@/slw/entities/notification/notification.mutation';
import { cn } from '@/slw/shared/lib/utils';

export const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: unreadCountData } = useUnreadCount();
  const markAllAsReadMutation = useMarkAllAsRead();
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAllAsRead = () => {
    startTransition(() => {
      markAllAsReadMutation.mutate(undefined, {
        onSuccess: () => toast.success('All notifications marked as read!'),
        onError: () => toast.error('Failed to mark all as read.'),
      });
    });
  };

  return (
    <div className='relative' ref={dropdownRef}>
      {/* Notification Bell */}
      <motion.button
        type='button'
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className='relative p-2 cursor-pointer text-white transition-colors
         duration-200 focus:outline-none rounded-md'
      >
        <Image src='/images/common/notice.png' alt='' width={20} height={22} priority />
        {unreadCountData?.data && unreadCountData?.data > 0 ? (
          <span
            className='absolute -top-1 -right-1 bg-red-500 text-white text-xs 
          rounded-full w-5 h-5 flex items-center justify-center font-medium animate-pulse'
          >
            {unreadCountData?.data > 99 ? '99+' : unreadCountData?.data}
          </span>
        ) : null}
      </motion.button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className='absolute right-0 mt-2 w-80 bg-white rounded-lg
         shadow-xl border border-gray-200 z-50 animate-fade-in'
        >
          <div className='p-4 border-b border-gray-100'>
            <div className='flex items-center justify-between'>
              <h3 className='text-lg font-semibold text-gray-900'>Notifications</h3>
              <button
                type='button'
                className={cn(
                  'text-sm text-blue-600 hover:text-blue-800 transition-colors duration-200',
                  !unreadCountData?.data ? 'opacity-50' : 'opacity-100',
                )}
                onClick={handleMarkAllAsRead}
                disabled={isPending || !unreadCountData?.data}
              >
                {isPending ? 'Marking...' : 'Mark all as read'}
              </button>
            </div>
          </div>

          <NotificationsList setIsOpen={setIsOpen} />
        </div>
      )}
    </div>
  );
};
